import { memo } from 'react';
import styled from 'styled-components';
import { Tooltip, Button, Select } from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  ReloadOutlined,
  ExpandOutlined,
  CompressOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useAtomValue, useSetAtom } from 'jotai';
import { availableFormsAtom, selectedFormUUIDAtom } from '../../atoms/CollectFormAtoms';

interface ToolbarProps {
  isMaximized: boolean;
  loading: boolean;
  collectItemsCount: number;
  recordCount: number;
  onOpenFilter: () => void;
  onOpenExport: () => void;
  onRefresh: () => void;
  onMaximize?: () => void;
  onClose?: () => void;
}

const Toolbar = ({
  isMaximized,
  loading,
  collectItemsCount,
  recordCount,
  onOpenFilter,
  onOpenExport,
  onRefresh,
  onMaximize,
  onClose,
}: ToolbarProps) => {
  const availableForms = useAtomValue(availableFormsAtom);
  const selectedFormUUID = useAtomValue(selectedFormUUIDAtom);
  const setSelectedFormUUID = useSetAtom(selectedFormUUIDAtom);

  const handleFormSelect = (formUuid: string) => {
    setSelectedFormUUID(formUuid);
  };

  const handleClearForm = () => {
    setSelectedFormUUID(null);
  };

  return (
    <ControlRow>
      <LeftControls>
        <Select
          placeholder="Chọn biểu mẫu..."
          style={{ width: '200px' }}
          size="small"
          showSearch
          value={selectedFormUUID}
          filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input.toLowerCase())}
          onChange={handleFormSelect}
          options={availableForms.map((form) => ({
            value: form.uuid,
            label: form.name || 'Chưa có tên',
          }))}
          loading={availableForms.length === 0}
        />
        <BackToFormsLink onClick={handleClearForm}>
          ← Chọn biểu mẫu khác
        </BackToFormsLink>
        <RecordCount>Số bản ghi: {recordCount}</RecordCount>
      </LeftControls>
      <ControlActions>
        <Tooltip title="Tìm kiếm">
          <Button type="text" size="small" icon={<SearchOutlined />} onClick={onOpenFilter} />
        </Tooltip>
        <Tooltip title="Tải xuống">
          <Button
            type="text"
            size="small"
            icon={<DownloadOutlined />}
            onClick={onOpenExport}
            disabled={loading || collectItemsCount === 0}
          />
        </Tooltip>
        <Tooltip title="Làm mới danh sách">
          <Button type="text" size="small" icon={<ReloadOutlined />} onClick={onRefresh} />
        </Tooltip>
        {onMaximize && (
          <Tooltip title={isMaximized ? 'Thu nhỏ' : 'Phóng to'}>
            <Button
              type="text"
              size="small"
              icon={isMaximized ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={onMaximize}
            />
          </Tooltip>
        )}
        {onClose && (
          <Tooltip title="Đóng">
            <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
          </Tooltip>
        )}
      </ControlActions>
    </ControlRow>
  );
};

export default memo(Toolbar);

const ControlRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 6px;

  @media (max-width: 768px) {
    padding: 4px 8px;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const RecordCount = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
`;

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ControlActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BackToFormsLink = styled.span`
  font-size: 12px;
  color: #3b82f6;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
`;
